import {Component, EventEmitter, inject, Input, OnChanges, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {merge} from 'rxjs';
import {
  GroupCardDescriptor,
  GroupCardModel,
  GroupMemberFieldDef,
  GroupMemberRow,
  GroupSubBlockDef,
  GroupSubBlockRow,
  defaultNamedTypeId,
  groupModelMemberRows,
  memberLabelInCard,
  memberValueSourceKind,
  NAMED_OBJECT_TYPE_LABEL_KEY,
  NamedObjectType,
  namedTakesNames,
  setMemberValueSourceKind,
} from './group-card.model';
import {NamedTypeOptionsService} from './named-type-options.service';
import {ObjectTypes} from '../../../core/definitions/object-types';
import {ConceptOption} from '../concept-picker/concept-option.model';
import {groupCardListTitle, GroupCardListTitle, headerOptionName} from './group-card-title';
import {ComposeLineRow} from '../compose-field/compose-field.model';
import {completeLookupKeys, LookupDefinition} from '../lookup-controls';
import {isConceptMember, memberFixedIsPick, memberRowSends} from '../import-payload';
import {unknownPlaceholders} from '../value-template';
import {CardRowValues, RowValues} from '../live-preview/live-preview';
import {
  bareTemplateColumn, ComposeLinesSummary, composeLinesSummary, rowValueSourceKinds, SourcePiece,
  VALUE_SOURCE_KIND_LABELS, VALUE_SOURCE_KIND_TOOLTIPS, ValueSourceKind, valueSourceIssue, valueSourceSummary,
} from '../value-source';

interface GroupCardColumn {
  name: string;
  sample_values: string[];
}

/** One field on a collapsed card's summary: its label and either, when
 * `fixed`, its fixed value or pick (`value`), or the `pieces` of the
 * source it is taken from. `row`: the member row it stands for, a
 * sub-block's member row included; none for a header pick. `incomplete`:
 * a Kodeoppslag or Sammensatt that sends nothing yet (`valueSourceIssue`).
 * `composeLines`: a Sammensatt's `pieces` a row each
 * (`composeLinesSummary`), for one with at least one line. */
export interface GroupCardSummaryItem {
  label: string;
  value: string;
  fixed: boolean;
  pieces: SourcePiece[];
  row?: GroupMemberRow;
  incomplete?: boolean;
  composeLines?: ComposeLinesSummary;
}

/** What a card reads off its model: its title, its collapsed summary,
 * and each member row's placeholders that name no column. */
interface GroupCardView {
  title: GroupCardListTitle;
  summary: GroupCardSummaryItem[];
  unknownPlaceholders: ReadonlyMap<GroupMemberRow, string[]>;
  warnsUnknownPlaceholders: boolean;
}

/**
 * One group of the Import module's mapping, of any `GroupKind`. Header =
 * the group's bounded concept selects; body = one row per member field,
 * each `[label] [Hentes fra] [the choice's inputs]`. A history event also
 * carries repeatable Named-party and Place sub-block lists.
 *
 * Presentational: the page owns the `GroupCardModel` (so control
 * subscriptions and payload composition live there); this component
 * renders it, mutates member templates in place, and emits add/remove.
 * Closed, the card is its title and a line per summary item; the page
 * decides which card is `opened`, and hears of an open or close through
 * `openedChange`.
 * The options of a Named sub-block's type picker are the one thing it
 * fetches itself (`NamedTypeOptionsService`).
 */
@Component({
  selector: 'app-group-card',
  templateUrl: './group-card.component.html',
  styleUrls: ['./group-card.component.scss'],
  standalone: false,
})
export class GroupCardComponent implements OnChanges {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly namedTypeOptions: NamedTypeOptionsService = inject(NamedTypeOptionsService);

  readonly objectTypes: NamedObjectType[] = [ObjectTypes.AGENT, ObjectTypes.NAMED_OBJECT];
  readonly sourceKindLabels = VALUE_SOURCE_KIND_LABELS;
  readonly sourceKindTooltips = VALUE_SOURCE_KIND_TOOLTIPS;
  // The type picker's options per object type, filled once the card has
  // a Named sub-block kind.
  typeOptions: Record<NamedObjectType, ConceptOption[]> = {
    [ObjectTypes.AGENT]: [], [ObjectTypes.NAMED_OBJECT]: [],
  };
  // The lists asked for and not failed; a failed one is asked for again.
  private readonly typeOptionsRequested = new Set<NamedObjectType>();
  // Moves when the translations the title and the summary come from do.
  private translationVersion = 0;
  private viewMemo: {key: unknown[]; view: GroupCardView} | null = null;

  constructor() {
    merge(this.translate.onLangChange, this.translate.onTranslationChange, this.translate.onFallbackLangChange)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.translationVersion++);
  }

  @Input() descriptor!: GroupCardDescriptor;
  @Input() model!: GroupCardModel;
  @Input() index = 0;
  // A setter, so `columnNames` is built once per list the page passes.
  @Input() set columns(columns: GroupCardColumn[]) {
    this._columns = columns;
    this.columnNames = new Set(columns.map(c => c.name));
  }
  get columns(): GroupCardColumn[] {
    return this._columns;
  }
  private _columns: GroupCardColumn[] = [];
  private columnNames = new Set<string>();
  // A member row's Kodeoppslag choice: the lookups it can take a value
  // through, each lookup table's columns once fetched, and the tables
  // whose columns are still on their way. No lookups, no Kodeoppslag.
  @Input() lookups: LookupDefinition[] = [];
  @Input() lookupColumns: { [table: string]: GroupCardColumn[] } = {};
  @Input() lookupColumnsLoading: ReadonlySet<string> = new Set<string>();
  @Input() opened = false;
  // The page's mapping version. Every control a card is built from moves
  // it, so what the card reads off its model is rebuilt only when it, an
  // input or the translations move.
  @Input({required: true}) mappingVersion!: number;
  // The page marks the card incomplete: a required pick or member is
  // missing, which holds the run back, or a member row's Kodeoppslag or
  // Sammensatt sends nothing, which does not.
  @Input() incomplete = false;
  // The required members the card sends no value onto, while that holds
  // the run back.
  @Input() missingMembers: readonly GroupMemberFieldDef[] = [];
  // The row the live preview shows, and what this card and its member rows
  // get in it; `null` while there is no preview or the card sends nothing.
  // `previewPending`: a newer preview is on its way.
  @Input() previewRow: number | null = null;
  @Input() preview: CardRowValues | null = null;
  @Input() previewPending = false;

  @Output() readonly addMember = new EventEmitter<string>();
  @Output() readonly removeMember = new EventEmitter<GroupMemberRow>();
  @Output() readonly addSubBlock = new EventEmitter<GroupSubBlockDef>();
  @Output() readonly removeSubBlock = new EventEmitter<GroupSubBlockRow>();
  @Output() readonly removeGroup = new EventEmitter<void>();
  // Compose-mode line edits on a member row. The page creates and drops
  // the lines, since it owns the control subscriptions.
  @Output() readonly addComposeLine = new EventEmitter<{row: GroupMemberRow; column?: string}>();
  @Output() readonly removeComposeLine = new EventEmitter<{row: GroupMemberRow; line: ComposeLineRow}>();
  @Output() readonly reorderComposeLines = new EventEmitter<{row: GroupMemberRow; lines: ComposeLineRow[]}>();
  @Output() readonly openedChange = new EventEmitter<boolean>();

  ngOnChanges(): void {
    this.requestTypeOptions();
  }

  /** Asks for each type list this card does not have yet, once it has a
   * Named sub-block kind. Called on every input change and whenever the
   * operator reaches for a type picker, so a list whose fetch failed is
   * asked for again. */
  requestTypeOptions(): void {
    if (!this.descriptor?.subBlocks.some(def => def.kind === 'named')) {
      return;
    }
    for (const objectType of this.objectTypes) {
      if (this.typeOptionsRequested.has(objectType)) {
        continue;
      }
      this.typeOptionsRequested.add(objectType);
      this.namedTypeOptions.options(objectType).then(
        options => this.typeOptions = {...this.typeOptions, [objectType]: options},
        () => this.typeOptionsRequested.delete(objectType));
    }
  }

  /** The card's title as one line of text. */
  get titleText(): string {
    return this.view.title.text;
  }

  /** The fields the collapsed card carries a value for, in card order: its
   * header picks - but the one the title is already named by - then its
   * member rows, each under its label in the card (`memberLabelInCard`),
   * then each sub-block's member under the sub-block's title. */
  get summary(): GroupCardSummaryItem[] {
    return this.view.summary;
  }

  /** `missingMembers` by their labels in the card, joined. */
  get missingMemberLabels(): string {
    return this.missingMembers.map(def => memberLabelInCard(this.descriptor, def.label)).join(', ');
  }

  /** Whether a member row, a sub-block's included, warns of placeholders
   * that name no column. */
  get warnsUnknownPlaceholders(): boolean {
    return this.view.warnsUnknownPlaceholders;
  }

  /** What the card reads off its model, the same while `mappingVersion`,
   * the inputs it is built from and the translations stay. */
  private get view(): GroupCardView {
    const key = [
      this.mappingVersion, this.model, this.descriptor, this.index, this.lookups, this._columns,
      this.translationVersion,
    ];
    const memo = this.viewMemo;
    if (memo && key.every((part, i) => part === memo.key[i])) {
      return memo.view;
    }
    const view = this.buildView();
    this.viewMemo = {key, view};
    return view;
  }

  private buildView(): GroupCardView {
    const instant = (key: string) => this.translate.instant(key);
    const title = groupCardListTitle(this.descriptor, this.model, this.index + 1, instant);
    const unknown = new Map(groupModelMemberRows(this.model).map(row => [row, this.findUnknownPlaceholders(row)]));
    return {
      title,
      summary: this.buildSummary(title),
      unknownPlaceholders: unknown,
      warnsUnknownPlaceholders: [...unknown.values()].some(names => names.length > 0),
    };
  }

  private buildSummary(title: GroupCardListTitle): GroupCardSummaryItem[] {
    const items: GroupCardSummaryItem[] = [];
    const titleName = title.typeName;
    for (const header of this.descriptor.header) {
      const name = headerOptionName(header, this.model.headerControls[header.key]?.value);
      if (name && name !== titleName) {
        items.push({label: this.translate.instant(header.label), value: name, fixed: true, pieces: []});
      }
    }
    const lookupKeys = completeLookupKeys(this.lookups);
    const add = (item: GroupCardSummaryItem | null) => {
      if (item) {
        items.push(item);
      }
    };
    for (const row of this.model.members) {
      const label = memberLabelInCard(this.descriptor, this.memberDef(row)?.label ?? '');
      add(this.summaryItem(label, row, isConceptMember(this.descriptor, row), lookupKeys));
    }
    for (const def of this.descriptor.subBlocks) {
      this.subBlockRows(def.kind).forEach((sub, i) => {
        add(this.summaryItem(`${this.translate.instant(def.titleKey)} ${i + 1}`, sub.member, true, lookupKeys));
      });
    }
    return items;
  }

  /** A member row's summary item, `null` for a row that is left out: one
   * the mapping sends no entry for (`memberRowSends`), unless it is a
   * Kodeoppslag or Sammensatt with an issue, which is listed as
   * incomplete. A fixed row names the typed value it sends; any other row
   * the source its choice reads (`valueSourceSummary`), a Sammensatt's a
   * row per template line (`composeLinesSummary`). A picked Named or
   * Place record (`memberFixedIsPick`) is an id here - its picker looks
   * its name up - so it shows only the fixed-value mark. */
  private summaryItem(
    label: string, row: GroupMemberRow, concept: boolean, lookupKeys: ReadonlySet<string>,
  ): GroupCardSummaryItem | null {
    const kind = this.sourceKind(row);
    const sends = memberRowSends(row, concept, this.columnNames, lookupKeys);
    if (!sends && !valueSourceIssue(row, kind, this.lookups)) {
      return null;
    }
    if (kind === 'constant') {
      const value = memberFixedIsPick(row, concept) ? '' : row.constantControl.value.trim();
      return {label, value, fixed: true, pieces: [], row};
    }
    const pieces = valueSourceSummary(row, kind, this.columnNames, this.lookups);
    const item: GroupCardSummaryItem = sends
      ? {label, value: '', fixed: false, pieces, row}
      : {label, value: '', fixed: false, pieces, row, incomplete: true};
    const composeLines = composeLinesSummary(pieces);
    return composeLines ? {...item, composeLines} : item;
  }

  sourceKind(row: GroupMemberRow): ValueSourceKind {
    return memberValueSourceKind(row);
  }

  /** The row's "Hentes fra" choices (`rowValueSourceKinds`): Kodeoppslag
   * while there is a lookup to take a value through, Sammensatt on a
   * free-text member. */
  sourceKinds(row: GroupMemberRow): readonly ValueSourceKind[] {
    return rowValueSourceKinds(this.sourceKind(row), this.lookups.length > 0, !!this.memberDef(row)?.composable);
  }

  /** Switches the row to `kind`, and on a switch to Sammensatt carries
   * the template over (`onComposeChosen`). */
  onSourceKindChange(row: GroupMemberRow, kind: ValueSourceKind): void {
    setMemberValueSourceKind(row, kind);
    if (kind === 'compose') {
      this.onComposeChosen(row);
    }
  }

  /** A row switched to Sammensatt with no lines carries its template over
   * as its first line when that is lossless (`bareTemplateColumn`). Any
   * other template stays where it is, untouched. */
  onComposeChosen(row: GroupMemberRow): void {
    if (!row.composeControl.value || row.composeRows.length) {
      return;
    }
    const column = bareTemplateColumn(row.templateControl.value, this.columnNames);
    if (column) {
      this.addComposeLine.emit({row, column});
    }
  }

  /** The member field defs the "Legg til felt" menu still offers: every
   * field not yet added, plus a `repeatable` one (the admin-event
   * `comments`) regardless of how many rows it already has - each one
   * becomes its own separate array item, not a shared slot. */
  get availableMemberFields(): GroupMemberFieldDef[] {
    const used = new Set(this.model.members.map(m => m.fieldId));
    return this.descriptor.members.filter(def => def.repeatable || !used.has(def.fieldId));
  }

  subBlockRows(kind: 'named' | 'place'): GroupSubBlockRow[] {
    return this.model.subBlocks.filter(row => row.defKind === kind);
  }

  memberDef(row: GroupMemberRow): GroupMemberFieldDef | undefined {
    const inSubBlock = this.descriptor.subBlocks.find(def => def.member.fieldId === row.fieldId);
    return inSubBlock?.member ?? this.descriptor.members.find(def => def.fieldId === row.fieldId);
  }

  /** Which repeatable sub-block `row` belongs to ('named' | 'place'), or
   * `undefined` for an ordinary group member - the fixed-value control
   * for a concept member differs by kind (a Named-party pick vs a Place
   * pick), so the template needs this to choose which picker to render. */
  subBlockKindOfMember(row: GroupMemberRow): 'named' | 'place' | undefined {
    return this.model.subBlocks.find(sub => sub.member === row)?.defKind;
  }

  /** Whether this concept member's value is a KulturNav objectid rather
   * than a name / picked id - a "column" value then rides
   * `value_is_kulturnav_uuid`, and a "fixed" value is a pasted UUID typed
   * into a plain field, not the named-party or Place picker. */
  isKulturNavUuid(row: GroupMemberRow): boolean {
    return !!this.memberDef(row)?.concept && row.valueTypeControl.value === 'kulturnav_uuid';
  }

  /** The Objekttype option's label. */
  objectTypeLabel(objectType: NamedObjectType): string {
    return objectType === ObjectTypes.NAMED_OBJECT
      ? 'TRANS__IMPORT__GROUP_CARD__OBJECT_TYPE__NAMED_OBJECT' : 'TRANS__IMPORT__GROUP_CARD__OBJECT_TYPE__AGENT';
  }

  /** The type picker's label: Aktørtype, or the named-object type. */
  typePickerLabel(objectType: NamedObjectType): string {
    return objectType === ObjectTypes.NAMED_OBJECT
      ? NAMED_OBJECT_TYPE_LABEL_KEY : 'TRANS__IMPORT__GROUP_CARD__AGENT_TYPE_PICKER';
  }

  /** Whether a Named sub-block shows its type picker: only while it takes
   * names from a column. A KulturNav uuid brings its record's own type,
   * and a fixed pick creates nothing. */
  showTypePicker(sub: GroupSubBlockRow): boolean {
    return namedTakesNames(sub);
  }

  /** A new object type starts over with its own default type, and drops a
   * fixed pick, which is a record of the other object type. */
  onObjectTypeChanged(sub: GroupSubBlockRow): void {
    const target = sub.target!;
    target.typeIdControl.setValue(defaultNamedTypeId(target.objectTypeControl.value));
    sub.member.conceptControl.setValue(null);
  }

  /** The object type a Named sub-block's fixed pick searches; Agent for a
   * row outside one. */
  namedObjectTypeOf(row: GroupMemberRow): NamedObjectType {
    return this.model.subBlocks.find(sub => sub.member === row)?.target?.objectTypeControl.value ?? ObjectTypes.AGENT;
  }

  /** What the collapsed card's line for summary item `index` shows in the
   * previewed row. A card of one line shows the card's values; on a card
   * of several, a member's line shows what its member row gets, and a
   * header pick's line the picked term, which every row gets. `null`
   * while there is no preview, or the row sends nothing or cannot be told. */
  lineValues(index: number): RowValues {
    const summary = this.summary;
    if (!this.preview || summary.length <= 1) {
      return this.preview?.values ?? null;
    }
    const item = summary[index];
    return item.row ? this.preview.members.get(item.row) ?? null : [item.value];
  }

  /** What member `row` gets in the previewed row; `undefined` while there
   * is no preview or the row sends nothing. */
  previewValuesOf(row: GroupMemberRow): RowValues | undefined {
    return this.preview?.members.get(row);
  }

  /** The template's placeholders that name no column, while the row reads
   * its template: Kolonne only. */
  unknownPlaceholders(row: GroupMemberRow): string[] {
    return this.view.unknownPlaceholders.get(row) ?? this.findUnknownPlaceholders(row);
  }

  private findUnknownPlaceholders(row: GroupMemberRow): string[] {
    return this.sourceKind(row) === 'column' ? unknownPlaceholders(row.templateControl.value, this.columnNames) : [];
  }
}
